import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../App';
import axios from 'axios';
import { toast } from 'sonner';
import PaymentModeIndicator from './PaymentModeIndicator';
import { 
  Users, 
  CheckCircle, 
  XCircle, 
  DollarSign,
  ArrowLeft,
  Clock,
  AlertCircle,
  TrendingUp,
  MessageSquare,
  Settings,
  MapPin,
  AlertTriangle
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminPanel = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [commissions, setCommissions] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [dashboard, setDashboard] = useState({});
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('dashboard');
  const [paymentMode, setPaymentMode] = useState('test'); // test or live

  useEffect(() => {
    fetchAdminData();
  }, []);

  const fetchAdminData = async () => {
    try {
      setLoading(true);
      
      // Fetch all admin data
      const [usersRes, jobsRes, commissionsRes, dashboardRes, notificationsRes] = await Promise.all([
        axios.get(`${API}/admin/users`),
        axios.get(`${API}/jobs`),
        axios.get(`${API}/admin/commissions`),
        axios.get(`${API}/admin/dashboard`),
        axios.get(`${API}/notifications`)
      ]);
      
      setUsers(usersRes.data);
      setJobs(jobsRes.data);
      setCommissions(commissionsRes.data);
      setDashboard(dashboardRes.data);
      setNotifications(notificationsRes.data);
    } catch (error) {
      console.error('Failed to fetch admin data:', error);
      toast.error('Failed to load admin data');
    } finally {
      setLoading(false);
    }
  };

  const handleApproveUser = async (userId) => {
    try {
      await axios.post(`${API}/admin/approve-user/${userId}`);
      toast.success('User approved successfully!');
      fetchAdminData();
    } catch (error) {
      console.error('Failed to approve user:', error);
      toast.error('Failed to approve user');
    }
  };

  const handleToggleCapability = async (userId, capability, value) => {
    try {
      const updateData = {};
      updateData[capability] = value;
      
      await axios.patch(`${API}/admin/users/${userId}`, updateData);
      
      const capabilityNames = {
        can_post_jobs: 'posting capability',
        can_receive_jobs: 'receiving capability',
        is_on_call: 'on-call status'
      };
      
      toast.success(`${capabilityNames[capability]} updated successfully!`);
      fetchAdminData();
    } catch (error) {
      console.error('Failed to update user capability:', error);
      toast.error('Failed to update user capability');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center">
              <button 
                onClick={() => navigate('/dashboard')} 
                className="mr-4 p-2 hover:bg-gray-100 rounded-lg"
              >
                <ArrowLeft className="h-5 w-5" />
              </button>
              <h1 className="text-xl font-bold text-gray-900">Admin Panel</h1>
              <PaymentModeIndicator mode={paymentMode} size="md" className="ml-4" />
            </div>
            
            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigate('/admin/support')}
                className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                <MessageSquare className="h-4 w-4 mr-2" />
                Support Inbox
              </button>
              <button
                onClick={() => navigate('/admin/jobs')}
                className="flex items-center px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
              >
                <Clock className="h-4 w-4 mr-2" />
                Bulk Jobs
              </button>
              <button
                onClick={() => navigate('/admin/addresses')}
                className="flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
              >
                <MapPin className="h-4 w-4 mr-2" />
                Address Validation
              </button>
              <button
                onClick={() => navigate('/admin/settings')}
                className="flex items-center px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
              >
                <Settings className="h-4 w-4 mr-2" />
                Settings
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Tab Navigation */}
        <div className="mb-8">
          <nav className="flex space-x-8">
            {[
              { id: 'dashboard', label: 'Dashboard', icon: TrendingUp },
              { id: 'users', label: 'Users', icon: Users },
              { id: 'jobs', label: 'Jobs', icon: Clock },
              { id: 'payments', label: 'Payments', icon: DollarSign },
              { id: 'commissions', label: 'Commissions', icon: DollarSign },
              { id: 'notifications', label: 'SMS Logs', icon: MessageSquare }
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center px-4 py-2 rounded-lg font-medium ${
                  activeTab === tab.id
                    ? 'bg-amber-100 text-amber-700'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                <tab.icon className="h-5 w-5 mr-2" />
                {tab.label}
              </button>
            ))}
          </nav>
        </div>

        {/* Dashboard Tab */}
        {activeTab === 'dashboard' && (
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <div className="jop-card p-6">
                <div className="flex items-center">
                  <div className="p-2 bg-blue-100 rounded-lg">
                    <Clock className="h-6 w-6 text-blue-600" />
                  </div>
                  <div className="ml-4">
                    <h3 className="text-lg font-semibold text-gray-900">{dashboard.total_jobs || 0}</h3>
                    <p className="text-sm text-gray-600">Total Jobs</p>
                  </div>
                </div>
              </div>
              
              <div className="jop-card p-6">
                <div className="flex items-center">
                  <div className="p-2 bg-green-100 rounded-lg">
                    <CheckCircle className="h-6 w-6 text-green-600" />
                  </div>
                  <div className="ml-4">
                    <h3 className="text-lg font-semibold text-gray-900">{dashboard.completed_jobs || 0}</h3>
                    <p className="text-sm text-gray-600">Completed</p>
                  </div>
                </div>
              </div>
              
              <div className="jop-card p-6">
                <div className="flex items-center">
                  <div className="p-2 bg-purple-100 rounded-lg">
                    <Users className="h-6 w-6 text-purple-600" />
                  </div>
                  <div className="ml-4">
                    <h3 className="text-lg font-semibold text-gray-900">{dashboard.total_users || 0}</h3>
                    <p className="text-sm text-gray-600">Total Users</p>
                  </div>
                </div>
              </div>
              
              <div className="jop-card p-6">
                <div className="flex items-center">
                  <div className="p-2 bg-amber-100 rounded-lg">
                    <DollarSign className="h-6 w-6 text-amber-600" />
                  </div>
                  <div className="ml-4">
                    <h3 className="text-lg font-semibold text-gray-900">
                      ${(dashboard.total_platform_commission || 0).toFixed(0)}
                    </h3>
                    <p className="text-sm text-gray-600">Platform Revenue</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="jop-card p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Platform Overview</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h4 className="font-medium text-gray-900 mb-2">User Statistics</h4>
                  <ul className="text-sm text-gray-600 space-y-1">
                    <li>Pending Approvals: {dashboard.pending_approvals || 0}</li>
                    <li>Active Jobs: {dashboard.active_jobs || 0}</li>
                    <li>Success Rate: {dashboard.total_jobs > 0 ? 
                      ((dashboard.completed_jobs / dashboard.total_jobs) * 100).toFixed(1) : 0}%
                    </li>
                  </ul>
                </div>
                <div>
                  <h4 className="font-medium text-gray-900 mb-2">Revenue Statistics</h4>
                  <ul className="text-sm text-gray-600 space-y-1">
                    <li>Total Originator Commissions: ${(dashboard.total_originator_commission || 0).toFixed(0)}</li>
                    <li>Average Job Value: ${dashboard.total_jobs > 0 ? 
                      ((dashboard.total_platform_commission + dashboard.total_originator_commission) / dashboard.total_jobs).toFixed(0) : 0}
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Users Tab */}
        {activeTab === 'users' && (
          <div className="jop-card p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">User Management</h3>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Business
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Email
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Post?
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Receive?
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      On-call?
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {users.map((user) => (
                    <tr key={user.id}>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">{user.business_name}</div>
                        {user.is_admin && (
                          <span className="text-xs text-purple-600 font-medium">Admin</span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">{user.email}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {!user.is_admin && (
                          <button
                            onClick={() => handleToggleCapability(user.id, 'can_post_jobs', !user.can_post_jobs)}
                            className={`w-10 h-6 rounded-full transition-colors ${
                              user.can_post_jobs ? 'bg-green-500' : 'bg-gray-300'
                            }`}
                          >
                            <div className={`w-4 h-4 bg-white rounded-full transition-transform ${
                              user.can_post_jobs ? 'translate-x-5' : 'translate-x-1'
                            }`}></div>
                          </button>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {!user.is_admin && (
                          <button
                            onClick={() => handleToggleCapability(user.id, 'can_receive_jobs', !user.can_receive_jobs)}
                            className={`w-10 h-6 rounded-full transition-colors ${
                              user.can_receive_jobs ? 'bg-green-500' : 'bg-gray-300'
                            }`}
                          >
                            <div className={`w-4 h-4 bg-white rounded-full transition-transform ${
                              user.can_receive_jobs ? 'translate-x-5' : 'translate-x-1'
                            }`}></div>
                          </button>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {!user.is_admin && user.can_receive_jobs && (
                          <button
                            onClick={() => handleToggleCapability(user.id, 'is_on_call', !user.is_on_call)}
                            className={`w-10 h-6 rounded-full transition-colors ${
                              user.is_on_call ? 'bg-blue-500' : 'bg-gray-300'
                            }`}
                          >
                            <div className={`w-4 h-4 bg-white rounded-full transition-transform ${
                              user.is_on_call ? 'translate-x-5' : 'translate-x-1'
                            }`}></div>
                          </button>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 py-1 text-xs rounded-full ${
                          user.is_approved ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                        }`}>
                          {user.is_approved ? 'Approved' : 'Pending'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        {!user.is_approved && (
                          <button
                            onClick={() => handleApproveUser(user.id)}
                            className="text-green-600 hover:text-green-900 mr-4"
                            title="Approve User"
                          >
                            <CheckCircle className="h-5 w-5" />
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Jobs Tab */}
        {activeTab === 'jobs' && (
          <div className="jop-card p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">All Jobs</h3>
            <div className="space-y-4">
              {jobs.map((job) => (
                <div key={job.id} className="border border-gray-200 rounded-lg p-4">
                  <div className="flex justify-between items-start">
                    <div>
                      <h4 className="font-medium text-gray-900">{job.title}</h4>
                      <p className="text-sm text-gray-600">{job.location} • ${job.quoted_price}</p>
                      <p className="text-sm text-gray-600">By: {job.originator_business}</p>
                      {job.accepted_by_business && (
                        <p className="text-sm text-gray-600">Accepted by: {job.accepted_by_business}</p>
                      )}
                    </div>
                    <div className="flex space-x-2">
                      <span className={`status-badge ${
                        job.status === 'posted' ? 'status-posted' :
                        job.status === 'accepted' ? 'status-accepted' :
                        job.status === 'completed' ? 'status-completed' : 'status-passed_back'
                      }`}>
                        {job.status.replace('_', ' ')}
                      </span>
                      <span className={`status-badge urgency-${job.urgency_level}`}>
                        {job.urgency_level}
                      </span>
                    </div>
                  </div>
                  <div className="mt-2 text-xs text-gray-500">
                    Created: {new Date(job.created_at).toLocaleString()}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Payments Tab */}
        {activeTab === 'payments' && (
          <div className="space-y-6">
            {/* Test/Live Mode Toggle */}
            <div className="jop-card p-6">
              <div className="flex justify-between items-center">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">Payment System Mode</h3>
                  <p className="text-sm text-gray-600">Configure payment processing mode</p>
                </div>
                
                <div className="flex items-center space-x-4">
                  <PaymentModeIndicator mode={paymentMode} size="md" />
                  
                  <div className="flex items-center">
                    <span className="text-sm text-gray-700 mr-3">Test</span>
                    <div className="relative">
                      <input
                        type="checkbox"
                        id="payment-mode-toggle"
                        checked={paymentMode === 'live'}
                        disabled={true}  // Disabled until Stripe credentials provided
                        className="sr-only"
                      />
                      <div className={`w-12 h-6 rounded-full cursor-not-allowed transition-colors ${
                        paymentMode === 'live' ? 'bg-green-500' : 'bg-gray-300'
                      }`}>
                        <div className={`w-5 h-5 bg-white rounded-full shadow transform transition-transform ${
                          paymentMode === 'live' ? 'translate-x-6 translate-y-0.5' : 'translate-x-0.5 translate-y-0.5'
                        }`}></div>
                      </div>
                    </div>
                    <span className="text-sm text-gray-700 ml-3">Live</span>
                  </div>
                  
                  <div className="text-xs text-amber-600 flex items-center">
                    <AlertTriangle className="h-3 w-3 mr-1" />
                    Stripe credentials required for Live Mode
                  </div>
                </div>
              </div>
              
              <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <h4 className="text-sm font-medium text-blue-900 mb-2">Current Mode: Test</h4>
                <div className="text-sm text-blue-700 space-y-1">
                  <p>• All payments are simulated (no real charges)</p>
                  <p>• Payment intents use test_ prefixes</p>
                  <p>• Commission calculations are accurate</p>
                  <p>• Full escrow flow available for testing</p>
                </div>
              </div>
            </div>

            {/* Payment Overview */}
            <div className="jop-card p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Payment Overview</h3>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                <div className="text-center">
                  <div className="text-2xl font-bold text-red-600">
                    {jobs.filter(job => job.payment_status === 'unfunded').length}
                  </div>
                  <div className="text-sm text-gray-600">Unfunded</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-green-600">
                    {jobs.filter(job => job.payment_status === 'funded').length}
                  </div>
                  <div className="text-sm text-gray-600">Funded</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-yellow-600">
                    {jobs.filter(job => job.payment_status === 'held').length}
                  </div>
                  <div className="text-sm text-gray-600">Held in Escrow</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-blue-600">
                    {jobs.filter(job => job.payment_status === 'released').length}
                  </div>
                  <div className="text-sm text-gray-600">Released</div>
                </div>
              </div>
            </div>

            {/* Payments Table */}
            <div className="jop-card p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Recent Payments</h3>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Job
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Amount
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Payment Status
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Originator
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Receiver
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Created
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {jobs.slice(0, 10).map((job) => (
                      <tr key={job.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900">
                            {job.title}
                          </div>
                          <div className="text-xs text-gray-500">
                            {`JOP-${job.id.slice(0, 8).toUpperCase()}`}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900">
                            ${job.quoted_price}
                          </div>
                          {job.receiver_amount && (
                            <div className="text-xs text-gray-500">
                              Net: ${job.receiver_amount}
                            </div>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                            job.payment_status === 'unfunded' ? 'bg-red-100 text-red-800' :
                            job.payment_status === 'funded' ? 'bg-green-100 text-green-800' :
                            job.payment_status === 'held' ? 'bg-yellow-100 text-yellow-800' :
                            job.payment_status === 'released' ? 'bg-blue-100 text-blue-800' :
                            'bg-purple-100 text-purple-800'
                          }`}>
                            {job.payment_status || 'unfunded'}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">{job.originator_business}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">
                            {job.accepted_by_business || 'Unassigned'}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">
                            {new Date(job.created_at).toLocaleDateString()}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* Commissions Tab */}
        {activeTab === 'commissions' && (
          <div className="jop-card p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Commission Tracking</h3>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Job Value
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Originator
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Platform
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Total
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Date
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {commissions.map((commission) => (
                    <tr key={commission.id}>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        ${commission.job_value.toFixed(2)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        ${commission.originator_commission.toFixed(2)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        ${commission.platform_commission.toFixed(2)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        ${commission.total_commission.toFixed(2)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {new Date(commission.calculated_at).toLocaleDateString()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Notifications Tab */}
        {activeTab === 'notifications' && (
          <div className="jop-card p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">SMS Notification Logs</h3>
            <div className="space-y-3">
              {notifications.map((notification) => (
                <div key={notification.id} className="border border-gray-200 rounded-lg p-4">
                  <div className="flex justify-between items-start">
                    <div>
                      <p className="text-sm font-medium text-gray-900">
                        To: {notification.recipient_phone}
                      </p>
                      <p className="text-sm text-gray-600 mt-1">
                        {notification.message}
                      </p>
                      <p className="text-xs text-gray-500 mt-1">
                        {new Date(notification.sent_at).toLocaleString()}
                      </p>
                    </div>
                    <span className={`px-2 py-1 text-xs rounded-full ${
                      notification.status === 'sent' ? 'bg-green-100 text-green-800' :
                      notification.status === 'delivered' ? 'bg-blue-100 text-blue-800' :
                      'bg-red-100 text-red-800'
                    }`}>
                      {notification.status}
                    </span>
                  </div>
                  {notification.twilio_sid && (
                    <p className="text-xs text-gray-400 mt-1">
                      Twilio SID: {notification.twilio_sid}
                    </p>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminPanel;